import { Box, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import {
  useDimensionPrecedence,
  carrierRungs,
  rungLabel,
} from '@/finance/useDimensionPrecedence';

/**
 * DimensionPrecedenceHint — one line under the dimension editor telling the person entering these
 * values where they rank when a plan line is derived.
 *
 * The values entered on a master-data record are DEFAULTS, and defaults compete: an assortment, a
 * customer, a trade type and a supplier can all sit on the same plan row and all want the same
 * dimension. Only one wins. That rule was previously invisible until someone opened the explain
 * dialog on a finished line and wondered why their value was struck through — this states it at the
 * point of entry instead.
 *
 * Renders only for records that actually occupy a rung (the ladder's `entity`), so forms whose
 * dimension set plays no part in plan derivation stay uncluttered. The order comes from the server;
 * nothing here knows it.
 */
export function DimensionPrecedenceHint({ entity }: { entity?: string }) {
  const { t } = useTranslation();
  const { data: ladder } = useDimensionPrecedence();

  if (!entity || !ladder?.length) return null;

  const own = ladder.find((r) => r.entity === entity);
  if (!own) return null; // this record's defaults are not part of plan derivation

  const carriers = carrierRungs(ladder);
  const isCarrier = carriers.some((r) => r.rank === own.rank);

  // A carrier competes with the other carriers; the org unit sits below all of them and is better
  // explained by what beats it than by a sibling order it has none of.
  const detail = isCarrier
    ? t('Finance.DimensionPrecedence.HintCarrier', {
        defaultValue:
          'If several records on the same row set the same dimension, this order decides: {{order}}.',
        order: carriers.map((r) => rungLabel(t, r)).join(' → '),
      })
    : t('Finance.DimensionPrecedence.HintOrgUnit', {
        defaultValue:
          'These apply last — a value entered on the row, pinned on the plan, or carried by the row’s own records takes precedence.',
      });

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1 }}>
      <InfoOutlinedIcon sx={{ fontSize: 14, mt: '2px', color: 'text.disabled', flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary">
        {t('Finance.DimensionPrecedence.HintIntro', {
          defaultValue: 'Used as a default when this record appears on a plan row.',
        })}{' '}
        <Tooltip title={t('Finance.DimensionPrecedence.HintStep', { defaultValue: 'Step {{step}} of the dimension precedence ladder', step: own.step })}>
          <span>{detail}</span>
        </Tooltip>
      </Typography>
    </Box>
  );
}
